// The maths and the model behind the Map view on Vehicle Allocation.
//
// WHAT THIS VIEW IS FOR. Fleetly holds exactly one piece of geography: where a
// driver lives, geocoded on v_driver_address_health. Vehicles hold none;
// vehicles.current_location is a status word ("Operating", "At Office"),
// not a place. So the map is a map of PEOPLE, drawn against the car each one is
// seated on.
//
// The number the map exists to surface is crew spread: the greatest straight
// line distance between any two drivers sharing a car. Four men rotate through
// one vehicle, so every shift change is a physical handover between two of them.
// A crew spread across 40 km is a cost Fleetly pays twice a day in time, fuel
// and late starts, and until now it was invisible because nothing in the
// cockpit put the four addresses next to each other.
//
// Straight line, not driving distance. Nothing here calls a routing API and
// nothing here should: the point is a comparison between cars, and a road
// factor would apply to all of them. Read the number as "how far apart these
// men live", never as "how far a driver travels".
//
// Rule 6 of the working agreement applies throughout: a missing figure is
// missing, never zero. A car with fewer than two mapped drivers has a null
// spread, not a spread of 0 km, because zero would read as "they live on the
// same street".
import type { AllocationSeat } from './allocation'
import type { AddressHealthRow } from './addressHealth'
import type { DriverBoardRow } from './managers'

export type LatLng = { lat: number; lng: number }

export type MapDriver = {
  driver_id: string
  driver_name: string
  lat: number
  lng: number
  // Null for a bench driver: they hold no seat in this run.
  vehicle_id: string | null
  plate: string | null
  seat_label: string | null
  shift_index: number | null
  is_planned: boolean
  manager_name: string | null
  address: string | null
  suburb_or_city: string | null
}

export type CarPlacement = {
  vehicle_id: string
  plate: string
  make: string | null
  model: string | null
  operational_state: string | null
  seats_total: number
  seats_filled: number
  // Drivers on this car that carry coordinates, sorted by seat.
  drivers: MapDriver[]
  // Seated drivers on this car with no usable coordinates. Named, not counted
  // away: the operator needs to know the spread below is computed on a partial
  // crew.
  unmapped: string[]
  // Null when fewer than two drivers are mapped. Never 0 as a stand-in.
  spread_km: number | null
  centre: LatLng | null
}

export type UnmappedDriver = {
  driver_id: string
  driver_name: string
  plate: string | null
  reason: string
}

export type AllocationMapModel = {
  cars: CarPlacement[]
  bench: MapDriver[]
  unmapped: UnmappedDriver[]
  // Every mapped point, seated and bench, for fitting the initial view.
  points: LatLng[]
}

const EARTH_KM = 6371.0088

function toRad(deg: number): number {
  return (deg * Math.PI) / 180
}

// Great-circle distance in kilometres. Haversine is accurate to well under a
// percent at Gauteng distances, which is far finer than the geocoding under it.
export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)))
}

// The greatest distance between any two points in the set. Null below two
// points: one man cannot be spread out, and reporting 0 would say he lives on
// top of a crew mate who does not exist.
export function crewSpreadKm(points: LatLng[]): number | null {
  if (points.length < 2) return null
  let worst = 0
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const d = haversineKm(points[i]!, points[j]!)
      if (d > worst) worst = d
    }
  }
  return worst
}

// Arithmetic mean of the points. Good enough for centring a map on a crew that
// lives within one metro; it is never presented as a depot or a meeting place.
export function centroid(points: LatLng[]): LatLng | null {
  if (points.length === 0) return null
  let lat = 0
  let lng = 0
  for (const p of points) {
    lat += p.lat
    lng += p.lng
  }
  return { lat: lat / points.length, lng: lng / points.length }
}

// Gauteng and the rest of South Africa. A geocode outside this box is a data
// fault, not a driver who moved: latitude here is negative and longitude
// positive, and the commonest failure is the two arriving swapped.
export function isPlausibleZaPoint(lat: unknown, lng: unknown): boolean {
  const la = Number(lat)
  const ln = Number(lng)
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return false
  return la <= -22 && la >= -35 && ln >= 16 && ln <= 33
}

// One line of place, for a marker tooltip. Suburb first, city second, neither
// invented.
function placeOf(row: AddressHealthRow | undefined): string | null {
  if (!row) return null
  return row.suburb || row.city || null
}

function addressOf(row: AddressHealthRow | undefined): string | null {
  if (!row) return null
  return row.current_address || row.onboarding_address || null
}

export function formatKm(km: number | null): string {
  if (km === null) return 'Not known'
  if (km < 10) return `${km.toFixed(1)} km`
  return `${Math.round(km)} km`
}

// Build everything the map draws from the reads the page already makes. No new
// endpoint: v_allocation_current gives the seats, v_driver_address_health gives
// the coordinates, v_allocation_bench gives who is unseated, and the driver
// manager board gives the name against each man.
export function buildAllocationMapModel(args: {
  seats: AllocationSeat[]
  addresses: AddressHealthRow[]
  bench: { driver_id: string; driver_name: string | null }[]
  driverBoard: DriverBoardRow[]
}): AllocationMapModel {
  const { seats, addresses, bench, driverBoard } = args

  const addressById = new Map<string, AddressHealthRow>()
  for (const a of addresses) addressById.set(a.driver_id, a)

  const managerById = new Map<string, string | null>()
  for (const d of driverBoard) managerById.set(d.driver_id, d.manager_name)

  const unmapped: UnmappedDriver[] = []
  const points: LatLng[] = []

  function toMapDriver(
    driverId: string,
    driverName: string,
    seat: AllocationSeat | null,
  ): MapDriver | null {
    const row = addressById.get(driverId)
    if (!row || !isPlausibleZaPoint(row.lat, row.lng)) {
      unmapped.push({
        driver_id: driverId,
        driver_name: driverName,
        plate: seat?.plate ?? null,
        reason: !row
          ? 'No address record'
          : row.lat === null || row.lng === null
            ? 'Address captured but never geocoded'
            : 'Geocode is outside South Africa, so it is wrong',
      })
      return null
    }
    const point = { lat: Number(row.lat), lng: Number(row.lng) }
    points.push(point)
    return {
      driver_id: driverId,
      driver_name: driverName,
      lat: point.lat,
      lng: point.lng,
      vehicle_id: seat?.vehicle_id ?? null,
      plate: seat?.plate ?? null,
      seat_label: seat?.seat_label ?? null,
      shift_index: seat?.shift_index ?? null,
      is_planned: seat?.is_planned === true,
      manager_name: managerById.get(driverId) ?? null,
      address: addressOf(row),
      suburb_or_city: placeOf(row),
    }
  }

  const carById = new Map<string, CarPlacement>()
  for (const s of seats) {
    let car = carById.get(s.vehicle_id)
    if (!car) {
      car = {
        vehicle_id: s.vehicle_id,
        plate: s.plate,
        make: s.make,
        model: s.model,
        operational_state: s.operational_state,
        seats_total: 0,
        seats_filled: 0,
        drivers: [],
        unmapped: [],
        spread_km: null,
        centre: null,
      }
      carById.set(s.vehicle_id, car)
    }
    car.seats_total += 1
    if (s.is_empty_slot || !s.driver_id) continue
    car.seats_filled += 1
    const d = toMapDriver(s.driver_id, s.driver_name || 'Unnamed driver', s)
    if (d) car.drivers.push(d)
    else car.unmapped.push(s.driver_name || 'Unnamed driver')
  }

  const cars = Array.from(carById.values())
  for (const car of cars) {
    car.drivers.sort((a, b) => (a.shift_index ?? 0) - (b.shift_index ?? 0))
    const pts = car.drivers.map((d) => ({ lat: d.lat, lng: d.lng }))
    car.spread_km = crewSpreadKm(pts)
    car.centre = centroid(pts)
  }

  // Widest crew first: the map opens on the problem, not on the alphabet. Cars
  // whose spread cannot be computed sort last, ordered by plate, because an
  // unknown is not a good score.
  cars.sort((a, b) => {
    if (a.spread_km === null && b.spread_km === null) return a.plate.localeCompare(b.plate)
    if (a.spread_km === null) return 1
    if (b.spread_km === null) return -1
    return b.spread_km - a.spread_km
  })

  const benchDrivers: MapDriver[] = []
  for (const b of bench) {
    const d = toMapDriver(b.driver_id, b.driver_name || 'Unnamed driver', null)
    if (d) benchDrivers.push(d)
  }
  benchDrivers.sort((a, b) => a.driver_name.localeCompare(b.driver_name))

  return { cars, bench: benchDrivers, unmapped, points }
}

// Median spread across the cars that have one. Median rather than mean: at
// fourteen cars a single outlier crew drags a mean somewhere no car actually is.
export function medianSpreadKm(cars: CarPlacement[]): number | null {
  const values = cars
    .map((c) => c.spread_km)
    .filter((v): v is number => v !== null)
    .sort((a, b) => a - b)
  if (values.length === 0) return null
  const mid = Math.floor(values.length / 2)
  return values.length % 2 === 1 ? values[mid]! : (values[mid - 1]! + values[mid]!) / 2
}
